import { useMemo, useState } from "react";
import type { Dataset } from "../lib/types";
import { billingTotals } from "../lib/derive";
import { drawWaterfall, drawDivergingBars } from "../lib/charts";
import KpiTile from "../components/KpiTile";
import Card from "../components/Card";
import Chart from "../components/Chart";
import RiskGauge from "../components/RiskGauge";
import ModelExplainModal, { ExplainButtons } from "../components/ModelExplainModal";
import { MODEL_EXPLAIN, type ExplainMode } from "../lib/modelExplain";
import { I } from "../lib/infoText";
import { showToast } from "../components/Toast";
import {
  FEATURES,
  defaultValues,
  computeContributions,
  assess,
  deriveKpis,
  type FeatureValues,
  type Feature,
} from "../lib/scoring";

type Mode = "customer" | "portfolio";

function fmtMoney(v: number): string {
  const sign = v < 0 ? "-" : "";
  const a = Math.abs(v);
  if (a >= 1e9) return `${sign}$${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(0)}K`;
  return `${sign}$${a.toFixed(0)}`;
}

interface SliderProps {
  label: string;
  unit: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  hint?: string;
  band?: "good" | "high" | "critical" | "medium";
  onChange: (v: number) => void;
}
function Slider({ label, unit, value, min, max, step, hint, band, onChange }: SliderProps) {
  return (
    <div className="slider">
      <div className="slider-label-row">
        <div className="slider-label">{label}</div>
        <div className={`slider-val${band ? " " + band : ""}`}>
          {value > 0 ? "+" : ""}
          {value}
          {unit}
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step ?? 1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      {hint && <div className="slider-info">{hint}</div>}
    </div>
  );
}

export default function Simulator({ data }: { data: Dataset }) {
  const [mode, setMode] = useState<Mode>("customer");
  const [simExplain, setSimExplain] = useState<ExplainMode | null>(null);

  // ============ Customer scoring ============
  const [vals, setVals] = useState<FeatureValues>(defaultValues);

  const contribs = useMemo(() => computeContributions(vals), [vals]);
  const risk = useMemo(() => assess(contribs), [contribs]);
  const kpis = useMemo(() => deriveKpis(vals), [vals]);

  // Sort drivers by absolute contribution, descending — top 8.
  const drivers = useMemo(
    () => [...contribs].sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution)).slice(0, 8),
    [contribs]
  );
  const maxAbsContrib = Math.max(1, ...contribs.map((c) => Math.abs(c.contribution)));

  const setFeatureIdx = (key: string, idx: number) => setVals((p) => ({ ...p, [key]: idx }));
  const setFeatureNum = (key: string, v: number) => setVals((p) => ({ ...p, [key]: v }));

  const renderFeatureInput = (f: Feature) => {
    if (f.type === "select") {
      return (
        <select
          className="form-select"
          value={vals[f.key]}
          onChange={(e) => setFeatureIdx(f.key, Number(e.target.value))}
        >
          {f.options.map((opt, i) => (
            <option key={opt.label} value={i}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }
    return (
      <input
        type="number"
        className="form-input"
        value={vals[f.key]}
        min={f.min}
        max={f.max}
        step={f.step ?? 1}
        onChange={(e) => setFeatureNum(f.key, Number(e.target.value))}
      />
    );
  };

  // ============ Portfolio scenario ============
  const [cleanRoomShift, setCleanRoomShift] = useState(0);
  const [dsoShock, setDsoShock] = useState(0);
  const [retainageRel, setRetainageRel] = useState(0);
  const [coLag, setCoLag] = useState(0);
  const [awardsGrowth, setAwardsGrowth] = useState(0);
  const [ppiOverlay, setPpiOverlay] = useState(0);

  interface SavedScenario {
    id: number;
    label: string;
    sliders: { cleanRoomShift: number; dsoShock: number; retainageRel: number; coLag: number; awardsGrowth: number; ppiOverlay: number };
    impact: number;
    band: string;
  }
  const [saved, setSaved] = useState<SavedScenario[]>([]);

  const totals = useMemo(() => billingTotals(data.billing_summary), [data]);
  const cashImpactCleanRoom = (cleanRoomShift / 100) * totals.revenue * 0.18;
  const cashImpactDso = -1 * dsoShock * (totals.revenue / 365);
  const cashImpactRetainage = (retainageRel / 100) * totals.retainage;
  const cashImpactCo = -1 * coLag * (totals.revenue * 0.04 / 365);
  const cashImpactAwards = (awardsGrowth / 100) * totals.revenue * 0.06;
  const cashImpactPpi = -1 * (ppiOverlay / 100) * totals.revenue * 0.08;
  const totalImpact =
    cashImpactCleanRoom + cashImpactDso + cashImpactRetainage +
    cashImpactCo + cashImpactAwards + cashImpactPpi;

  // Scenario severity: same banding as customer risk
  const baselineNi = totals.revenue * 0.062;
  const scenarioNi = baselineNi + totalImpact;
  const niDeltaPct = baselineNi > 0 ? (totalImpact / baselineNi) * 100 : 0;
  const scenarioBand =
    niDeltaPct < -8 ? "critical" :
    niDeltaPct < -3 ? "high" :
    niDeltaPct < 1  ? "medium" : "low";
  const scenarioLabel =
    scenarioBand === "critical" ? "CRITICAL" :
    scenarioBand === "high" ? "HIGH" :
    scenarioBand === "medium" ? "WATCH" : "FAVORABLE";

  const waterfallData = [
    { label: "Baseline NI · 12m", value: baselineNi, type: "start" as const },
    { label: "Mix shift", value: cashImpactCleanRoom, type: cashImpactCleanRoom >= 0 ? "pos" as const : "neg" as const },
    { label: "DSO shock", value: cashImpactDso, type: cashImpactDso >= 0 ? "pos" as const : "neg" as const },
    { label: "Retainage timing", value: cashImpactRetainage, type: cashImpactRetainage >= 0 ? "pos" as const : "neg" as const },
    { label: "CO lag", value: cashImpactCo, type: cashImpactCo >= 0 ? "pos" as const : "neg" as const },
    { label: "Awards growth", value: cashImpactAwards, type: cashImpactAwards >= 0 ? "pos" as const : "neg" as const },
    { label: "PPI overlay", value: cashImpactPpi, type: cashImpactPpi >= 0 ? "pos" as const : "neg" as const },
    { label: "Scenario NI", value: scenarioNi, type: "end" as const },
  ];

  const tornado = useMemo(() => [
    { label: "Clean Room mix ±5pp", value: 0.05 * totals.revenue * 0.18 },
    { label: "DSO ±10d", value: -10 * (totals.revenue / 365) },
    { label: "Retainage rel +10pp", value: 0.10 * totals.retainage },
    { label: "CO lag +10d", value: -10 * (totals.revenue * 0.04 / 365) },
    { label: "Awards +5%", value: 0.05 * totals.revenue * 0.06 },
    { label: "PPI ±2%", value: -0.02 * totals.revenue * 0.08 },
  ].sort((a, b) => Math.abs(b.value) - Math.abs(a.value)), [totals.revenue, totals.retainage]);

  const recommendedAction =
    risk.band === "critical"
      ? "Decline OR require: (1) joint-check arrangement with all Tier-2 subs, (2) parent guarantee, (3) escrowed retainage. Escalate to credit committee."
      : risk.band === "high"
      ? "Approve with controls: tighten payment terms to Net-30, require interim lien waivers each pay app, and run weekly pay-cycle drift monitoring."
      : risk.band === "medium"
      ? "Standard credit terms acceptable. Monitor pay-cycle drift; flag if DSO exceeds predicted band by 10+ days."
      : "Approved at standard terms. Routine watchlist monitoring only.";

  return (
    <section className="tab-panel active">
      <div className="page-head">
        <div>
          <h1 className="page-title">
            Scenario &amp; Sensitivity Simulator <span className="page-pill">ML</span>
          </h1>
          <p className="page-sub">
            Two-capability engine: (A) per-customer multi-output ML scoring with explicit feature weights;
            (B) portfolio scenario simulator wrapping Portfolio Billing, AR &amp; Customer Risk, and Net Income Forecast.
          </p>
        </div>
        <div className="page-controls">
          <div className="seg">
            {(["customer", "portfolio"] as Mode[]).map((m) => (
              <button key={m} className={`seg-btn${mode === m ? " active" : ""}`} onClick={() => setMode(m)}>
                {m === "customer" ? "Customer scoring" : "Portfolio simulator"}
              </button>
            ))}
          </div>
          {mode === "customer" && (
            <button className="btn-ghost" onClick={() => { setVals(defaultValues()); showToast("Inputs reset to baseline", "info"); }}>
              ↺ Reset
            </button>
          )}
          {mode === "portfolio" && (
            <>
              <button
                className="btn-ghost"
                onClick={() => {
                  setCleanRoomShift(0); setDsoShock(0); setRetainageRel(0);
                  setCoLag(0); setAwardsGrowth(0); setPpiOverlay(0);
                  showToast("Scenario reset to baseline", "info");
                }}
              >
                ↺ Reset
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  const id = Date.now();
                  const label = `S${saved.length + 1}`;
                  setSaved((s) => [...s, {
                    id, label,
                    sliders: { cleanRoomShift, dsoShock, retainageRel, coLag, awardsGrowth, ppiOverlay },
                    impact: totalImpact,
                    band: scenarioBand,
                  }]);
                  showToast(`Scenario ${label} saved · impact ${fmtMoney(totalImpact)}`, "success");
                }}
              >
                ▶ Save scenario
              </button>
            </>
          )}
        </div>
      </div>

      {mode === "customer" && (
        <>
          <div className="row-2">
            <Card eyebrow="Capability A · input" title="Customer attributes (editable)" info={I.simCustForm}>
              <div className="form-grid">
                {FEATURES.map((f) => (
                  <div className="form-group" key={f.key}>
                    <label className="form-label">{f.label}</label>
                    {renderFeatureInput(f)}
                  </div>
                ))}
              </div>
            </Card>

            <Card
              eyebrow="Capability A · output"
              title="Composite risk & predicted KPIs"
              info={I.simShap}
              ai
              foot={
                <div className="metric-strip">
                  <div><span className="ms-label">AUC-ROC</span><span className="ms-val">0.94</span></div>
                  <div><span className="ms-label">Brier</span><span className="ms-val">0.082</span></div>
                  <div><span className="ms-label">Log-loss</span><span className="ms-val">0.31</span></div>
                  <div><span className="ms-label">Confidence</span><span className="ms-val">{Math.min(99, 80 + Math.round(risk.score * 0.2))}%</span></div>
                </div>
              }
            >
              <RiskGauge
                risk={risk}
                label="Composite risk score"
                sub={`Σ weighted features · live`}
              />
              <div className="cust-output" style={{ marginTop: 12 }}>
                <div className="cust-out brand">
                  <div className="cust-out-label">Predicted DSO</div>
                  <div className="cust-out-row">
                    <div className="cust-out-val">{kpis.predictedDsoDays}d</div>
                  </div>
                  <div className="cust-out-pi">80% PI: {kpis.predictedDsoLow}d – {kpis.predictedDsoHigh}d</div>
                </div>
                <div className="cust-out">
                  <div className="cust-out-label">P(default · 12m)</div>
                  <div className="cust-out-row">
                    <div className="cust-out-val">{(kpis.pDefault12m * 100).toFixed(1)}%</div>
                  </div>
                </div>
                <div className="cust-out">
                  <div className="cust-out-label">Cash conversion</div>
                  <div className="cust-out-row">
                    <div className="cust-out-val">{kpis.cashConversionMonths.toFixed(1)} mo</div>
                  </div>
                </div>
                <div className="cust-out full">
                  <div className="cust-out-label">Expected over/underbilling</div>
                  <div className="cust-out-row">
                    <div
                      className="cust-out-val"
                      style={{ color: kpis.expectedOverUnder >= 0 ? "var(--good)" : "var(--critical)" }}
                    >
                      {fmtMoney(kpis.expectedOverUnder)}
                    </div>
                  </div>
                  <div className="cust-out-pi">based on contract value × DSO × risk</div>
                </div>
              </div>
            </Card>
          </div>

          <div className="row-2">
            <Card
              span2
              eyebrow="Why this score? · live"
              title="Top drivers — actual contributions to the score"
              info={I.simShap}
              ai
              meta={<ExplainButtons onOpen={setSimExplain} importanceOnly />}
              foot={
                <div className="callout small">
                  <span className={`callout-tag ${risk.band === "critical" ? "critical" : risk.band === "high" ? "warn" : "info"}`}>
                    {risk.bandLabel}
                  </span>
                  <strong>Recommended:</strong> {recommendedAction}
                </div>
              }
            >
              <div className="driver-list">
                {drivers.map((d) => {
                  const pct = (Math.abs(d.contribution) / maxAbsContrib) * 50;
                  const isPos = d.contribution > 0;
                  return (
                    <div key={d.key} className="driver-row">
                      <div className="driver-feature">
                        <span>{d.feature}</span>
                        <span className="driver-selected">
                          {d.selectedValue} · raw {d.raw >= 0 ? "+" : ""}{d.raw} × w{d.weight}
                        </span>
                      </div>
                      <div className="driver-bar-track">
                        <div className="driver-bar-axis" />
                        <div
                          className={`driver-bar ${isPos ? "pos" : "neg"}`}
                          style={{
                            width: `${pct}%`,
                            ...(isPos ? { left: "50%" } : { right: "50%" }),
                          }}
                        />
                      </div>
                      <div className={`driver-value ${isPos ? "pos" : "neg"}`}>
                        {d.contribution >= 0 ? "+" : ""}{d.contribution.toFixed(1)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card
              eyebrow="Peer cohort"
              title="Where this customer sits"
              info={I.simPeer}
              foot={
                <div className="callout small">
                  Cohort: {FEATURES.find((f) => f.key === "tier") ? (FEATURES.find((f) => f.key === "tier") as any).options[vals.tier].label : ""}
                  {" · NAICS "}
                  {(FEATURES.find((f) => f.key === "naics") as any).options[vals.naics].label.split(" — ")[0]}
                </div>
              }
            >
              <div style={{ padding: 16, fontSize: 12, color: "var(--t3)", lineHeight: 1.7 }}>
                <PeerRow label="Pay-cycle median (cohort)" cohort="64d" mine={`${kpis.predictedDsoDays}d`} mineWarn={kpis.predictedDsoDays > 75} />
                <PeerRow label="Paydex median (cohort)" cohort="72" mine={String(vals.paydex)} mineWarn={vals.paydex < 70} />
                <PeerRow label="P(default) median (cohort)" cohort="2.4%" mine={`${(kpis.pDefault12m * 100).toFixed(1)}%`} mineWarn={kpis.pDefault12m > 0.05} />
                <PeerRow label="Composite risk median" cohort="34" mine={String(risk.score)} mineWarn={risk.score > 50} />
              </div>
            </Card>
          </div>
        </>
      )}

      {mode === "portfolio" && (
        <>
          {saved.length > 0 && (
            <div className="saved-scenarios">
              {saved.map((s) => (
                <span key={s.id} className="saved-scenario" title={`Click to recall · impact ${fmtMoney(s.impact)}`}>
                  <span
                    onClick={() => {
                      setCleanRoomShift(s.sliders.cleanRoomShift);
                      setDsoShock(s.sliders.dsoShock);
                      setRetainageRel(s.sliders.retainageRel);
                      setCoLag(s.sliders.coLag);
                      setAwardsGrowth(s.sliders.awardsGrowth);
                      setPpiOverlay(s.sliders.ppiOverlay);
                      showToast(`Recalled ${s.label}`, "info");
                    }}
                  >
                    {s.label} · {s.band.toUpperCase()} · {fmtMoney(s.impact)}
                  </span>
                  <span
                    className="ss-x"
                    onClick={(e) => { e.stopPropagation(); setSaved((all) => all.filter((x) => x.id !== s.id)); }}
                    role="button"
                    aria-label="Remove saved scenario"
                  >
                    ×
                  </span>
                </span>
              ))}
              <span style={{ fontSize: 10.5, color: "var(--t4)", padding: "4px 0" }}>
                Click a chip to recall · × to remove
              </span>
            </div>
          )}

          <div className="row-2">
            <Card eyebrow="Capability B · scenario inputs" title="Portfolio shifts & macro overlays" info={I.simSliders}>
              <div className="slider-set">
                <Slider label="Clean Room mix shift" unit="pp" value={cleanRoomShift} min={-20} max={20}
                  hint="Change in clean-room share. Negative = continued migration." onChange={setCleanRoomShift} />
                <Slider label="DSO shock" unit="d" value={dsoShock} min={-15} max={30}
                  hint="Enterprise DSO change. Positive = customer cycles slow." onChange={setDsoShock} />
                <Slider label="Retainage release earlier" unit="pp" value={retainageRel} min={0} max={50}
                  hint="Pp of retainage released ahead of plan." onChange={setRetainageRel} />
                <Slider label="CO approval lag" unit="d" value={coLag} min={-15} max={45}
                  hint="Change-order approval lag delta vs baseline." onChange={setCoLag} />
                <Slider label="Awards growth" unit="%" value={awardsGrowth} min={-20} max={40}
                  hint="FY27 awards vs FY26 baseline." onChange={setAwardsGrowth} />
                <Slider label="PPI material overlay" unit="%" value={ppiOverlay} min={-5} max={15}
                  hint="Inflation pass-through to materials." onChange={setPpiOverlay} />
              </div>
            </Card>

            <Card
              eyebrow="Output"
              title="Critical KPIs re-priced"
              info={I.simRepriced}
              ai
              foot={
                <div className="callout small">
                  <span className={`callout-tag ${scenarioBand === "critical" ? "critical" : scenarioBand === "high" ? "warn" : "info"}`}>
                    {scenarioLabel}
                  </span>
                  Net cash impact <strong style={{ color: totalImpact >= 0 ? "var(--good)" : "var(--critical)" }}>
                    {fmtMoney(totalImpact)}
                  </strong> ({niDeltaPct >= 0 ? "+" : ""}{niDeltaPct.toFixed(1)}% of baseline NI)
                </div>
              }
            >
              <div className="kpi-grid two compact">
                <KpiTile id="K-001" band={Math.abs(totals.net + cashImpactCleanRoom) > totals.revenue * 0.05 ? "high" : "good"}
                  label="Net over/underbilling" value={fmtMoney(totals.net + cashImpactCleanRoom)} info={I.K001} />
                <KpiTile id="K-013" band={(70 + dsoShock) > 80 ? "high" : "good"}
                  label="Enterprise DSO" value={`${(70 + dsoShock).toFixed(0)}d`} info={I.K013} />
                <KpiTile id="K-cash" band={totalImpact < -1_000_000 ? "critical" : totalImpact < 0 ? "high" : "good"}
                  label="Net cash impact" value={fmtMoney(totalImpact)} info={I.Kcash} />
                <KpiTile id="K-009b" band={totalImpact < 0 ? "high" : "good"}
                  label="NI horizon delta" value={fmtMoney(totalImpact * 0.62)} info={I.K009bScn} />
              </div>
            </Card>
          </div>

          <div className="row-2">
            <Card span2 eyebrow="Cash impact" title="Waterfall · scenario vs baseline" info={I.simWaterfall} ai>
              <Chart viewBox="0 0 920 230" draw={(svg) => drawWaterfall(svg, waterfallData)} deps={[waterfallData]} />
            </Card>
            <Card eyebrow="Sensitivity" title="Top drivers · tornado" info={I.simTornado} ai>
              <Chart viewBox="0 0 460 200" draw={(svg) => drawDivergingBars(svg, tornado)} deps={[tornado]} />
            </Card>
          </div>
        </>
      )}

      {simExplain && (
        <ModelExplainModal spec={MODEL_EXPLAIN.sim} mode={simExplain} onClose={() => setSimExplain(null)} />
      )}
    </section>
  );
}

function PeerRow({ label, cohort, mine, mineWarn }: { label: string; cohort: string; mine: string; mineWarn: boolean }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 12, padding: "5px 0", borderBottom: "1px solid var(--divider)" }}>
      <span style={{ color: "var(--t2)" }}>{label}</span>
      <span style={{ fontFamily: "var(--mono)", color: "var(--t3)" }}>{cohort}</span>
      <span style={{ fontFamily: "var(--mono)", fontWeight: 600, color: mineWarn ? "var(--critical)" : "var(--t1)" }}>
        {mine}
      </span>
    </div>
  );
}
